import { createUploadFile } from "./postFileAccess";
import { supportsMediaPublishing } from "./platformCapabilities";
import { unsupportedMedia } from "./unsupportedMedia";
import { fetch as expoFetch } from "expo/fetch";
import { manipulateAsync, SaveFormat, type Action } from "expo-image-manipulator";
import {
  deleteObject,
  getDownloadURL,
  ref,
} from "firebase/storage";
import { app, auth, firebaseEmulatorMode, storage } from "./firebase";
import {
  cleanupCurrentPostImagePaths,
  classifyPendingImageAsset,
  createPostUploadError,
  readUploadBytes,
  waitForUpload,
} from "./postUploadCore";
import { mapWithConcurrency } from "./postPublishCore";
import type { PostAttachmentType } from "../types/models";
export { createPostUploadError } from "./postUploadCore";

export const MAX_POST_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_POST_PDF_BYTES = 20 * 1024 * 1024;
export const MAX_POST_IMAGES = 6;
const MAX_NORMALIZED_IMAGE_SIDE = 1400;
const NORMALIZED_IMAGE_COMPRESSION = 0.7;
const UPLOAD_TIMEOUT_MS = 60_000;
const CLEANUP_TIMEOUT_MS = 15_000;

export type PendingAttachment = {
  uri: string;
  type: PostAttachmentType;
  name?: string;
  mimeType?: string;
  size?: number;
  width?: number;
  height?: number;
};

export type UploadedPostImage = {
  url: string;
  storagePath: string;
  width?: number;
  height?: number;
  contentType: string;
};

export type PostUploadAuthSession = {
  token: string;
  refreshed?: boolean;
  refreshPromise?: Promise<string>;
};

async function refreshUploadAuthSession(session: PostUploadAuthSession) {
  const user = auth.currentUser;
  if (!user) return false;
  if (!session.refreshPromise) {
    if (session.refreshed) return false;
    session.refreshed = true;
    session.refreshPromise = user.getIdToken(true);
  }
  session.token = await session.refreshPromise;
  return true;
}

type PendingImageAsset = {
  uri?: string | null;
  fileName?: string | null;
  mimeType?: string | null;
  fileSize?: number | null;
  width?: number | null;
  height?: number | null;
};

const MAX_STORAGE_FILE_NAME_LENGTH = 96;

function sanitizeName(name: string) {
  const clean = name
    .replace(/[^a-zA-Z0-9._-]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");

  return clean || `file_${Date.now()}`;
}

function truncateName(name: string) {
  if (name.length <= MAX_STORAGE_FILE_NAME_LENGTH) return name;

  const dotIndex = name.lastIndexOf(".");
  const ext = dotIndex > 0 ? name.slice(dotIndex) : "";
  const stem = dotIndex > 0 ? name.slice(0, dotIndex) : name;
  const maxStemLength = Math.max(12, MAX_STORAGE_FILE_NAME_LENGTH - ext.length);

  return `${stem.slice(0, maxStemLength)}${ext}`;
}

function ensureExtension(name: string, ext: string) {
  if (!ext) return name;
  if (name.toLowerCase().endsWith(`.${ext.toLowerCase()}`)) return name;
  if (name.includes(".")) return name;
  return `${name}.${ext}`;
}

function forceJpegName(name?: string) {
  const clean = sanitizeName(String(name || `image_${Date.now()}.jpg`));
  const dotIndex = clean.lastIndexOf(".");
  const stem = dotIndex > 0 ? clean.slice(0, dotIndex) : clean;
  return truncateName(`${stem || `image_${Date.now()}`}.jpg`);
}

function inferExtension(type: PostAttachmentType, mimeType?: string, name?: string) {
  const lowerName = String(name ?? "").toLowerCase();
  if (lowerName.includes(".")) {
    return lowerName.split(".").pop() ?? (type === "pdf" ? "pdf" : "jpg");
  }
  if (mimeType === "application/pdf") return "pdf";
  if (mimeType === "image/png") return "png";
  if (mimeType === "image/webp") return "webp";
  return type === "pdf" ? "pdf" : "jpg";
}

function defaultContentType(attachment: PendingAttachment) {
  if (attachment.type === "image") return "image/jpeg";
  return attachment.mimeType ?? "application/pdf";
}

function maxBytesForAttachment(type: PostAttachmentType) {
  return type === "pdf" ? MAX_POST_PDF_BYTES : MAX_POST_IMAGE_BYTES;
}

export function formatAttachmentSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
  if (bytes >= 1024) return `${Math.ceil(bytes / 1024)} Ko`;
  return `${bytes} o`;
}

function assertAttachmentSize(attachment: PendingAttachment, size?: number) {
  if (!Number.isFinite(size ?? NaN)) return;

  if ((size as number) <= 0) {
    throw createPostUploadError("post/file-empty", "Le fichier est vide.");
  }

  const maxBytes = maxBytesForAttachment(attachment.type);
  if ((size as number) > maxBytes) {
    throw createPostUploadError(
      "post/file-too-large",
      attachment.type === "pdf"
        ? `PDF trop lourd (${formatAttachmentSize(size as number)}). Limite: ${formatAttachmentSize(MAX_POST_PDF_BYTES)}.`
        : `Photo trop lourde (${formatAttachmentSize(size as number)}). Limite: ${formatAttachmentSize(MAX_POST_IMAGE_BYTES)}.`
    );
  }
}

export async function getUploadBody(attachment: PendingAttachment) {
  if (!supportsMediaPublishing) unsupportedMedia();
  assertAttachmentSize(attachment, attachment.size);
  return readUploadBytes({
    uri: attachment.uri,
    declaredSize: attachment.size,
    maxBytes: maxBytesForAttachment(attachment.type),
    createFile: createUploadFile,
  });
}

function getResizeAction(width?: number, height?: number, maxSide = MAX_NORMALIZED_IMAGE_SIDE): Action[] {
  if (!width || !height) return [];

  const longestSide = Math.max(width, height);
  if (longestSide <= maxSide) return [];

  if (width >= height) {
    return [{ resize: { width: maxSide } }];
  }

  return [{ resize: { height: maxSide } }];
}

async function sizeOfUri(uri: string) {
  try {
    const file = createUploadFile(uri);
    return file.exists ? file.size : undefined;
  } catch {
    return undefined;
  }
}

export async function normalizeImageForUpload(
  attachment: PendingAttachment
): Promise<PendingAttachment> {
  if (!supportsMediaPublishing) unsupportedMedia();
  if (attachment.type !== "image") return attachment;

  if (__DEV__) console.log("upload:image:stage", {
    stage: "image-manipulation",
    status: "start",
    size: attachment.size,
    width: attachment.width,
    height: attachment.height,
  });

  let result;
  try {
    result = await manipulateAsync(
      attachment.uri,
      getResizeAction(attachment.width, attachment.height),
      {
        compress: NORMALIZED_IMAGE_COMPRESSION,
        format: SaveFormat.JPEG,
      }
    );
  } catch (error) {
    throw createPostUploadError(
      "post/image-manipulation-failed",
      "Impossible de convertir cette image.",
      error
    );
  }
  const size = await sizeOfUri(result.uri);

  assertAttachmentSize({ ...attachment, type: "image" }, size);

  if (__DEV__) console.log("upload:image:stage", {
    stage: "image-manipulation",
    status: "complete",
    size,
    contentType: "image/jpeg",
    width: result.width,
    height: result.height,
  });

  return {
    ...attachment,
    uri: result.uri,
    type: "image",
    name: forceJpegName(attachment.name),
    mimeType: "image/jpeg",
    size,
    width: result.width,
    height: result.height,
  };
}

export function pendingImagesFromAssets(assets: PendingImageAsset[], maxCount: number) {
  const attachments: PendingAttachment[] = [];
  let rejectedInvalid = 0;
  let rejectedTooLarge = 0;

  for (let index = 0; index < assets.length; index += 1) {
    if (attachments.length >= maxCount) break;

    const asset = assets[index];
    const classification = classifyPendingImageAsset(asset ?? {}, MAX_POST_IMAGE_BYTES);
    if (classification === "invalid") {
      rejectedInvalid += 1;
      continue;
    }
    if (classification === "too-large") {
      rejectedTooLarge += 1;
      continue;
    }
    if (!asset?.uri) {
      rejectedInvalid += 1;
      continue;
    }

    const size =
      typeof asset.fileSize === "number" && Number.isFinite(asset.fileSize)
        ? asset.fileSize
        : undefined;

    attachments.push({
      uri: asset.uri,
      type: "image",
      name: asset.fileName ?? `image_${Date.now()}_${index + 1}.jpg`,
      mimeType: asset.mimeType ?? "image/jpeg",
      size,
      width:
        typeof asset.width === "number" && Number.isFinite(asset.width)
          ? asset.width
          : undefined,
      height:
        typeof asset.height === "number" && Number.isFinite(asset.height)
          ? asset.height
          : undefined,
    });

    if (__DEV__) console.log("upload:image:stage", {
      stage: "image-picker",
      status: "complete",
      index,
      size,
      contentType: asset.mimeType ?? "unknown",
      width: asset.width ?? undefined,
      height: asset.height ?? undefined,
    });
  }

  return { attachments, rejectedInvalid, rejectedTooLarge };
}

async function uploadBlob(params: {
  uid: string;
  attachment: PendingAttachment;
  storagePath: string;
  token?: string;
  authSession?: PostUploadAuthSession;
  signal?: AbortSignal;
  onMetric?: (stage: "read" | "upload" | "download-url", durationMs: number) => void;
}) {
  const { attachment } = params;
  const readStartedAt = Date.now();
  const uploadBody = await getUploadBody(attachment);
  params.onMetric?.("read", Date.now() - readStartedAt);

  const ext = inferExtension(attachment.type, attachment.mimeType, attachment.name);
  const rawName = ensureExtension(
    attachment.name?.trim() || `${attachment.type}_${Date.now()}.${ext}`,
    ext
  );
  const baseName = truncateName(sanitizeName(rawName));
  const storagePath = params.storagePath;
  const contentType = defaultContentType(attachment);

  const storageRef = ref(storage, storagePath);
  let storageUploadCompleted = false;
  const cleanupAfterUpload = async () => {
    if (!storageUploadCompleted) return;
    try {
      await waitForUpload({
        upload: deleteObject(storageRef),
        timeoutMs: CLEANUP_TIMEOUT_MS,
      });
    } catch (cleanupError) {
      if (__DEV__) console.error("upload:cleanup:error", {
        code: (cleanupError as { code?: string })?.code ?? "unknown",
        storagePath,
      });
    }
  };

  if (__DEV__) console.log("upload:image:stage", {
    stage: "file-read",
    status: "complete",
    size: uploadBody.byteLength,
    contentType,
  });
  if (__DEV__) console.log("upload:image:stage", {
    stage: "storage-upload-start",
    size: uploadBody.byteLength,
    contentType,
  });

  const controller = new AbortController();
  const forwardAbort = () => controller.abort();
  if (params.signal?.aborted) controller.abort();
  params.signal?.addEventListener("abort", forwardAbort, { once: true });
  try {
    const uploadStartedAt = Date.now();
    try {
      await waitForUpload({
        upload: uploadBytesWithExpoFetch({
          storagePath,
          uploadBody,
          contentType,
          token: params.authSession?.token ?? params.token,
          signal: controller.signal,
        }),
        timeoutMs: UPLOAD_TIMEOUT_MS,
        controller,
        signal: params.signal,
      });
      storageUploadCompleted = true;
      params.onMetric?.("upload", Date.now() - uploadStartedAt);
    } finally {
      params.signal?.removeEventListener("abort", forwardAbort);
    }
  } catch (error) {
    await cleanupAfterUpload();
    const value = error as { code?: unknown };
    if (typeof value?.code === "string") throw error;
    throw createPostUploadError("storage/unknown", "L'envoi du fichier a echoue.", error);
  }

  if (__DEV__) console.log("upload:image:stage", {
    stage: "storage-upload-complete",
    size: uploadBody.byteLength,
    contentType,
  });

  let downloadURL: string;
  try {
    const urlStartedAt = Date.now();
    downloadURL = await waitForUpload({
      upload: getDownloadURL(storageRef),
      timeoutMs: UPLOAD_TIMEOUT_MS,
      signal: params.signal,
    });
    params.onMetric?.("download-url", Date.now() - urlStartedAt);
  } catch (error) {
    const downloadError = createPostUploadError(
      "post/download-url-failed",
      "L'adresse de l'image n'a pas pu etre obtenue.",
      error
    );
    await cleanupAfterUpload();
    throw downloadError;
  }

  if (__DEV__) console.log("upload:image:stage", {
    stage: "download-url",
    status: "complete",
  });

  return {
    url: downloadURL,
    name: attachment.name || baseName,
    storagePath,
    contentType,
  };
}

async function uploadBytesWithExpoFetch(params: {
  storagePath: string;
  uploadBody: Uint8Array;
  contentType: string;
  token?: string;
  signal?: AbortSignal;
}) {
  const user = auth.currentUser;
  if (!user) {
    throw createPostUploadError(
      "storage/unauthenticated",
      "Une session est requise pour envoyer le fichier."
    );
  }

  const bucket = app.options.storageBucket;
  if (!bucket) {
    throw createPostUploadError("storage/unknown", "Le bucket Storage est indisponible.");
  }

  const token = params.token ?? await user.getIdToken();
  const storageRestBaseUrl = firebaseEmulatorMode
    ? "http://127.0.0.1:9199"
    : "https://firebasestorage.googleapis.com";
  const boundary = `${Math.random().toString().slice(2)}${Math.random().toString().slice(2)}`;
  const metadata = JSON.stringify({
    name: params.storagePath,
    contentType: params.contentType,
  });
  const lineBreak = String.fromCharCode(13, 10);
  const multipartPreamble =
    `--${boundary}${lineBreak}` +
    `Content-Type: application/json; charset=utf-8${lineBreak}${lineBreak}` +
    `${metadata}${lineBreak}` +
    `--${boundary}${lineBreak}` +
    `Content-Type: ${params.contentType}${lineBreak}${lineBreak}`;
  const multipartClosing = `${lineBreak}--${boundary}--`;
  const encoder = new TextEncoder();
  const preambleBytes = encoder.encode(multipartPreamble);
  const closingBytes = encoder.encode(multipartClosing);
  const multipartBody = new Uint8Array(
    preambleBytes.byteLength + params.uploadBody.byteLength + closingBytes.byteLength
  );
  multipartBody.set(preambleBytes, 0);
  multipartBody.set(params.uploadBody, preambleBytes.byteLength);
  multipartBody.set(
    closingBytes,
    preambleBytes.byteLength + params.uploadBody.byteLength
  );
  const uploadUrl =
    `${storageRestBaseUrl}/v0/b/${encodeURIComponent(bucket)}/o` +
    `?name=${encodeURIComponent(params.storagePath)}`;
  const response = await expoFetch(uploadUrl, {
    method: "POST",
    headers: {
      Authorization: `Firebase ${token}`,
      "Content-Type": `multipart/related; boundary=${boundary}`,
      "X-Goog-Upload-Protocol": "multipart",
      ...(app.options.appId ? { "X-Firebase-GMPID": app.options.appId } : {}),
    },
    // expo/fetch accepts ArrayBuffer views natively; React Native's BodyInit
    // declaration does not currently include them.
    body: multipartBody as unknown as BodyInit,
    signal: params.signal,
  });

  if (response.ok) return;

  const code =
    response.status === 401
      ? "storage/unauthenticated"
      : response.status === 402
        ? "storage/quota-exceeded"
      : response.status === 403
        ? "storage/unauthorized"
        : "storage/unknown";
  throw createPostUploadError(
    code,
    `Firebase Storage a refuse l'envoi (HTTP ${response.status}).`
  );
}

function createImageId(index: number) {
  const random = Math.random().toString(36).slice(2, 12);
  return `${Date.now().toString(36)}_${index}_${random}`;
}

export async function uploadPostImages(params: {
  uid: string;
  postId: string;
  images: PendingAttachment[];
  token?: string;
  authSession?: PostUploadAuthSession;
  signal?: AbortSignal;
  normalize?: boolean;
  onUploaded?: (index: number, image: UploadedPostImage) => void | Promise<void>;
  onMetric?: (index: number, stage: "read" | "upload" | "download-url", durationMs: number) => void;
  storageFileNames?: string[];
}): Promise<UploadedPostImage[]> {
  const uploads: UploadedPostImage[] = [];
  const authSession = params.authSession ?? {
    token: params.token ?? await auth.currentUser?.getIdToken() ?? "",
  };
  const poolController = new AbortController();
  const forwardAbort = () => poolController.abort();
  params.signal?.addEventListener("abort", forwardAbort, { once: true });

  try {
    const results = await mapWithConcurrency({
      items: params.images,
      concurrency: 2,
      signal: poolController.signal,
      worker: async (sourceImage, index) => {
      if (__DEV__) console.log("upload:image:start", {
        index,
        size: sourceImage.size,
        contentType: sourceImage.mimeType,
        width: sourceImage.width,
        height: sourceImage.height,
      });

      const requestedName = params.storageFileNames?.[index];
      const fileName = requestedName && /^[A-Za-z0-9_-]{1,128}$/.test(requestedName)
        ? requestedName
        : createImageId(index);
      const storagePath = `moderation-pending/${params.uid}/${params.postId}/${fileName}.jpg`;
      let uploaded;
      let normalizedImage: PendingAttachment;
      try {
        normalizedImage = params.normalize === false
          ? sourceImage
          : await normalizeImageForUpload(sourceImage);
        try {
          uploaded = await uploadBlob({
            uid: params.uid,
            attachment: normalizedImage,
            storagePath,
            authSession,
            signal: poolController.signal,
            onMetric: (stage, durationMs) => params.onMetric?.(index, stage, durationMs),
          });
        } catch (error) {
          const code = (error as { code?: string }).code;
          if (code !== "storage/unauthenticated" || !(await refreshUploadAuthSession(authSession))) throw error;
          uploaded = await uploadBlob({
            uid: params.uid,
            attachment: normalizedImage,
            storagePath,
            authSession,
            signal: poolController.signal,
            onMetric: (stage, durationMs) => params.onMetric?.(index, stage, durationMs),
          });
        }
      } catch (error) {
        const firebaseError = error as { code?: unknown; message?: unknown };
        const code = typeof firebaseError?.code === "string" ? firebaseError.code : "unknown";
        const stage =
          code === "post/image-manipulation-failed"
            ? "image-manipulation"
            : code.startsWith("post/file-")
              ? "file-read"
              : code === "post/download-url-failed"
                ? "download-url"
                : "storage-upload";
      if (__DEV__) console.error("upload:image:error", {
          code,
          stage,
        });
        poolController.abort();
        throw error;
      }

      const result = {
        // Pending URLs are intentionally not persisted in a post document.
        url: "",
        storagePath: uploaded.storagePath,
        width: normalizedImage.width,
        height: normalizedImage.height,
        contentType: uploaded.contentType,
      } satisfies UploadedPostImage;
      uploads.push(result);
      await params.onUploaded?.(index, result);

      if (__DEV__) console.log("upload:image:end", {
        index,
        size: normalizedImage.size,
        contentType: uploaded.contentType,
        width: normalizedImage.width,
        height: normalizedImage.height,
      });
      return result;
      },
    });
    return results;
  } catch (error) {
    try {
      await cleanupCurrentPostImages({
        uid: params.uid,
        postId: params.postId,
        storagePaths: uploads.map((image) => image.storagePath),
      });
    } catch (cleanupError) {
      const value = cleanupError as { code?: unknown; message?: unknown };
      if (__DEV__) console.error("upload:image:cleanup:error", {
        code: typeof value?.code === "string" ? value.code : "unknown",
        stage: "cleanup",
      });
      // Preserve the upload error; best-effort cleanup must not replace it.
    }
    throw error;
  } finally {
    params.signal?.removeEventListener("abort", forwardAbort);
  }
}

export async function getPostAttachmentDownloadURL(storagePath: string, signal?: AbortSignal) {
  return waitForUpload({
    upload: getDownloadURL(ref(storage, storagePath)),
    timeoutMs: UPLOAD_TIMEOUT_MS,
    signal,
  });
}

export async function cleanupCurrentPostImages(params: {
  uid: string;
  postId: string;
  storagePaths: string[];
}): Promise<void> {
  await cleanupCurrentPostImagePaths({
    ...params,
    remove: async (storagePath) =>
      waitForUpload({
        upload: deleteObject(ref(storage, storagePath)),
        timeoutMs: CLEANUP_TIMEOUT_MS,
      }),
  });
}

export function isCurrentPostPdfPath(path: string, uid: string, postId: string) {
  const legacyPrefix = `post_attachments/${uid}/`;
  const legacySuffix = path.slice(legacyPrefix.length);
  const pendingPrefix = `moderation-pending/${uid}/${postId}/`;
  const pendingSuffix = path.slice(pendingPrefix.length);
  return (
    (path.startsWith(legacyPrefix) && legacySuffix.length > 4 && !legacySuffix.includes("/") && legacySuffix.endsWith(".pdf"))
    || (path.startsWith(pendingPrefix) && pendingSuffix.length > 4 && !pendingSuffix.includes("/") && pendingSuffix.endsWith(".pdf"))
  );
}

export async function cleanupCurrentPostPdf(params: {
  uid: string;
  postId: string;
  storagePath?: string;
}) {
  if (!params.storagePath || !isCurrentPostPdfPath(params.storagePath, params.uid, params.postId)) return;
  await waitForUpload({
    upload: deleteObject(ref(storage, params.storagePath)),
    timeoutMs: CLEANUP_TIMEOUT_MS,
  });
}

export async function uploadPostPdf(params: {
  uid: string;
  attachment: PendingAttachment;
  storagePath: string;
  token?: string;
  authSession?: PostUploadAuthSession;
  signal?: AbortSignal;
  onMetric?: (stage: "read" | "upload" | "download-url", durationMs: number) => void;
}) {
  if (!params.storagePath.startsWith(`moderation-pending/${params.uid}/`)) {
    throw createPostUploadError(
      "storage/unauthorized",
      "Un nouveau PDF doit être envoyé dans la zone de modération.",
    );
  }

  const authSession = params.authSession ?? {
    token: params.token ?? await auth.currentUser?.getIdToken() ?? "",
  };
  const upload = () => uploadBlob({
    uid: params.uid,
    attachment: params.attachment,
    storagePath: params.storagePath,
    authSession,
    signal: params.signal,
    onMetric: params.onMetric,
  });
  let uploaded;
  try {
    uploaded = await upload();
  } catch (error) {
    const code = (error as { code?: string }).code;
    if (code !== "storage/unauthenticated" || !(await refreshUploadAuthSession(authSession))) throw error;
    uploaded = await upload();
  }

  return {
    attachmentType: "pdf" as const,
    // The pending object has no feed URL until the Functions worker approves it.
    attachmentUrl: "",
    attachmentStoragePath: uploaded.storagePath,
    attachmentName: uploaded.name,
  };
}
